import { useState } from 'react'

import { Button, ColorUnion, OptionSelector } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import IncompleteProcessesBanner from 'modules/processes/components/IncompleteProcessesBanner'
import { useAssignableProcesses } from 'modules/processes/hooks/useAssignableProcesses'
import { useBulkCreateMaterializedProcess } from 'modules/processes/hooks/useBulkCreateMaterializedProcess'
import { useTemplate } from 'modules/processes/hooks/useTemplate'

interface Props {
  currentUserId: string
  onClose: () => void
}

interface CreateProcessFormProps {
  workflowId: string | null
  templateId: string | undefined
  customProcessesInputFields: { [key: string]: string }
}

const initialFormState = {
  workflowId: null,
  templateId: undefined,
  customProcessesInputFields: {},
}

type Value = string | number | null | undefined

const AddEmployeeToProcess = ({ onClose, currentUserId }: Props) => {
  const { company } = useCurrentSession()
  const [userId] = useState(currentUserId)
  const { loading, processes, areAllAssignable } = useAssignableProcesses()
  const { loading: submitting, bulkCreateMaterializedProcesses } =
    useBulkCreateMaterializedProcess()

  const options = processes.map((process) => ({
    name: process.name,
    value: process.id,
    disabled: process.disabled,
    tag: process.disabled
      ? {
          type: 'champagne' as ColorUnion, // eslint-disable-line no-type-assertion/no-type-assertion
          text: process.validationMessage ?? '',
        }
      : undefined,
  }))

  const { values, setValues, setErrors, handleSubmit } = useForm<CreateProcessFormProps>({
    defaultValues: initialFormState,
    onSubmit,
  })

  const { template } = useTemplate(values.templateId)
  const processInputFields = template?.processInputFields
  const customProcessInputFields = (processInputFields || []).filter((field) => field.custom)

  async function onSubmit(data: CreateProcessFormProps) {
    if (!data.workflowId) return {} as Result<unknown> // eslint-disable-line no-type-assertion/no-type-assertion

    const entries = Object.entries(data.customProcessesInputFields)

    const items =
      entries.length > 0
        ? entries.map(([key, value]) => ({ key, value, dataType: 'string' }))
        : null

    const result = await bulkCreateMaterializedProcesses({
      companyId: company.id,
      processId: data.workflowId,
      resourceIds: [userId],
      processInputFields: items,
    })

    const handled = handleResult(result, (errors) => {
      setErrors(errors)
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('processes.tasks.import.form.success'),
      })
      onClose()
    }

    return handled
  }

  const handleCustomFieldChange = (key: string, value: string) => {
    setValues({
      ...values,
      customProcessesInputFields: {
        ...values.customProcessesInputFields,
        [key]: value,
      },
    })
  }

  const completedMandatoryField = customProcessInputFields.every((field) => {
    return (
      field.mandatory === false ||
      Object.keys(values.customProcessesInputFields).includes(field.key)
    )
  })

  const disabled = !completedMandatoryField || submitting || !values.workflowId

  const onSelect = (item: { name: string; disabled?: boolean; value: Value }) => {
    if (!item.value || item.disabled) return
    if (item.value === values.workflowId) return setValues({ ...values, workflowId: null })
    setValues({
      ...values,
      workflowId: String(item.value),
      templateId: processes.find((p) => p.id === item.value)?.template?.id,
    })
  }

  const noProcesses = processes.length === 0

  return (
    <Modal
      illustration="tasks_management"
      title={i18n.t('processes.tasks.import.form.title')}
      description={i18n.t('processes.tasks.import.form.description')}
      onClose={onClose}
      onSubmit={() => handleSubmit()}
      footer={
        <Button disabled={disabled} submit>
          {i18n.t('processes.tasks.import.form.submit')}
        </Button>
      }
    >
      {loading && <Loading />}
      {!loading && !noProcesses && (
        <OptionSelector
          selectedValue={values.workflowId}
          items={options}
          noItems={i18n.t('processes.tasks.import.form.not_found')}
          onSelect={onSelect}
        />
      )}
      {customProcessInputFields.length > 0 &&
        customProcessInputFields.map((field) => {
          if (field.data_type == 'date')
            return (
              <DayPicker
                key={field.key}
                mandatory={field.mandatory}
                label={field.label}
                readonly={submitting}
                mode="single"
                onChange={(value) => handleCustomFieldChange(field.key, value)}
                value={values.customProcessesInputFields[field.key]}
              />
            )

          return (
            <TextField
              key={field.key}
              mandatory={field.mandatory}
              readonly={submitting}
              label={field.label}
              onChange={(value) => handleCustomFieldChange(field.key, value)}
              value={values.customProcessesInputFields[field.key]}
            />
          )
        })}
      {!loading && (
        <IncompleteProcessesBanner
          loading={loading}
          processes={processes}
          areAllAssignable={areAllAssignable}
        />
      )}
    </Modal>
  )
}

export default observer(AddEmployeeToProcess)
